// @ts-strict-ignore
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'src/store/store';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Origin, Section } from 'src/constants/footfall';
import { createPost, editPost, getTags } from 'src/modules/community/write/actions';
import { showModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { validateBody, validateTitle } from 'src/modules/community/write/validate';
import { useUnsavedChangesPrompt } from 'src/utils/hooks/useUnsavedChangesPrompt';
import { helpScoutArticle } from 'src/utils/helpScoutBeacon';
import ContentEditor from 'src/components/textEditorSimple/components/contentEditor';
import Spacer from 'src/components/spacer';
import { EMAIL_IN_TEXT, PHONE_NUMBER, URL_RE } from 'src/constants/regex';
import { Counter, EditorContainer, SubmitButtonWrapper, PostContainer, SubmitButton, TipsButtonContainer, TipsButton } from 'src/modules/community/write/components/sharedStyles';
import { addHTMLMentionsSimpleEditor } from 'src/components/textEditor/deserialiseContent';
import { getLocalStorageItem, Key } from 'src/utils/localStorage';
import WritePostErrors from 'src/modules/community/write/components/writePostErrors';
import { Data } from 'src/components/textEditor/customTypes';
export default function WritePostEditorAndroid({
  children
}: {
  children: ReactNode;
}) {
  const dispatch = useDispatch();
  const {
    post,
    htmlTitleString,
    htmlBodyString
  } = useSelector(state => state.pages.write);
  const editorRefTitle = useRef();
  const editorRefPost = useRef();
  const postContainerRef = useRef<HTMLDivElement>();
  const [titleData, setTitleData] = useState<Data>({
    body: '',
    image: {
      url: null,
      hash: null,
      file: null,
      description: null,
      children: null
    },
    mentionedUsers: []
  });
  const [bodyData, setBodyData] = useState<Data>({
    body: '',
    image: {
      url: null,
      hash: null,
      file: null,
      description: null,
      children: null
    },
    mentionedUsers: []
  });
  const [titleCounter, setTitleCounter] = useState(0);
  const countCharsTitle = data => setTitleCounter(data?.body?.length || 0);
  const debouncedGetTags = useCallback(debounce((title, body) => {
    dispatch(getTags(title, body));
  }, 5000), []);
  const onChangeGetTags = () => {
    if (titleData?.body?.length >= 5 && bodyData?.body?.length >= 20) {
      debouncedGetTags(titleData.body, bodyData.body);
    }
  };
  const [canValidate, setCanValidate] = useState(!!htmlTitleString?.length);
  const [errors, setErrors] = useState({
    title: [],
    body: []
  });
  const [isDirty, setIsDirty] = useState({
    title: false,
    body: false
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const debouncedValidateTitle = useCallback(debounce(() => {
    if (canValidate) {
      const titleErrors = validateTitle({
        text: titleData.body || '',
        setIsDirty: newState => setIsDirty(prevState => ({
          ...prevState,
          title: newState
        }))
      });
      setErrors(prevState => ({
        ...prevState,
        title: titleErrors
      }));
    }
  }, 500), [titleData]);
  const debouncedValidateBody = useCallback(debounce(() => {
    if (canValidate) {
      const bodyErrors = validateBody({
        text: bodyData.body || '',
        setIsDirty: newState => setIsDirty(prevState => ({
          ...prevState,
          body: newState
        }))
      });
      setErrors(prevState => ({
        ...prevState,
        body: bodyErrors
      }));
    }
  }, 500), [bodyData]);
  debouncedValidateTitle();
  debouncedValidateBody();
  const trackSubmit = () => {
    trackFootfallEvent(Event.Clicked, {
      clickMetadata: {
        origin: post?.id ? Origin.EditPostPage : Origin.WritePostPage
      },
      clickType: 'submit'
    });
  };
  useEffect(() => {
    // both create and edit
    if (isSubmit) {
      const revalidateErrorsTitle = validateTitle({
        text: titleData.body
      });
      const revalidateErrorsBody = validateBody({
        text: bodyData.body
      });
      if (revalidateErrorsTitle.length || revalidateErrorsBody.length && !revalidateErrorsBody[0].submitAllowed) {
        setErrors({
          body: revalidateErrorsBody,
          title: revalidateErrorsTitle
        });
        setIsSubmit(false);
      } else {
        const postData = {
          title: titleData.body,
          body: bodyData.body,
          mentionedUserIds: bodyData.mentionedUsers.map(({
            userId
          }) => userId),
          image: bodyData.image
        };
        const doSubmit = post?.id // edit post
        ? editPost(postData) : createPost(postData);
        if (!getLocalStorageItem(Key.privacyReminderDismissed) && (EMAIL_IN_TEXT.test(titleData.body) || PHONE_NUMBER.test(titleData.body) && !URL_RE.test(titleData.body) || EMAIL_IN_TEXT.test(bodyData.body) || PHONE_NUMBER.test(bodyData.body) && !URL_RE.test(bodyData.body) || !!bodyData.image?.file)) {
          // only triggers modal when new image is being uploaded
          dispatch(showModal({
            modalType: ModalType.PrivacyReminder,
            modalProps: {
              onAccept: () => {
                dispatch(doSubmit);
                trackSubmit();
              },
              onCancel: () => setIsSubmit(false),
              data: {
                text: titleData.body + ' ' + bodyData.body,
                image: bodyData?.image.file ? bodyData.image : null
              }
            }
          }));
          return;
        }
        dispatch(doSubmit);
        trackSubmit();
      }
    }
  }, [isSubmit]);
  useEffect(() => {
    return () => {
      setIsReset(true);
      setIsSubmit(false);
      setCanValidate(false);
    };
  }, []);
  useUnsavedChangesPrompt({
    unsavedChanges: !isSubmit && (isDirty.title || isDirty.body)
  });
  return <>
      <EditorContainer $hasErrors={!!errors.title?.length} aria-label="Text editor for post title" data-sentry-element="EditorContainer" data-sentry-source-file="writePostEditorAndroid.tsx">
        <ContentEditor autoFocus={true} dataTestId="android-title-editor" hasMentions={false} hasTextFormat={false} hasToolbar={false} initialHtml={htmlTitleString} isReset={isReset} isTitle={true} placeholder={i18n.t('Title\n')} ref={editorRefTitle} onChange={() => {
        if (isReset) setIsReset(false);
        if (isSubmit) setIsSubmit(false);
      }} onChanged={(data: Data) => {
        if (!canValidate && data.body.length) {
          setCanValidate(true);
        }
        setTitleData(data);
        onChangeGetTags();
        countCharsTitle(data);
      }} data-sentry-element="ContentEditor" data-sentry-source-file="writePostEditorAndroid.tsx" />
        <Counter data-sentry-element="Counter" data-sentry-source-file="writePostEditorAndroid.tsx">{titleCounter}/140</Counter>
        <TipsButtonContainer data-sentry-element="TipsButtonContainer" data-sentry-source-file="writePostEditorAndroid.tsx">
          <TipsButton dataTestId="privacy-tips-button" icon="information" text={i18n.t('Post tips')} type="link" onClick={() => {
          helpScoutArticle('190-posting-tips');
          trackFootfallEvent(Event.Clicked, {
            clickMetadata: {
              section: Section.WritePostTips,
              origin: post?.id ? Origin.EditPostPage : Origin.WritePostPage
            },
            clickType: 'viewed'
          });
        }} data-sentry-element="TipsButton" data-sentry-source-file="writePostEditorAndroid.tsx" />
        </TipsButtonContainer>
      </EditorContainer>
      <WritePostErrors dataTestId="write-post-title-error" errors={errors.title} data-sentry-element="WritePostErrors" data-sentry-source-file="writePostEditorAndroid.tsx" />
      <Spacer data-sentry-element="Spacer" data-sentry-source-file="writePostEditorAndroid.tsx" />
      <PostContainer $hasErrors={!!errors.body?.length} aria-label="Text editor for post body" data-testid="write-post-body" ref={postContainerRef} data-sentry-element="PostContainer" data-sentry-source-file="writePostEditorAndroid.tsx">
        <ContentEditor autoFocus={false} dataTestId="android-body-editor" imgWithDescription={false} initialHtml={htmlBodyString && addHTMLMentionsSimpleEditor(htmlBodyString, post?.mentionedUsers || []) || ''} initialImage={post?.postImageHash && {
        hash: post?.postImageHash,
        url: post?.imageUrl,
        children: null
      }} isReset={isReset} placeholder={i18n.t('Tell your story...')} ref={editorRefPost} onAddImg={() => postContainerRef.current?.scrollIntoView({
        behavior: 'smooth'
      })} onChange={() => {
        if (isReset) setIsReset(false);
      }} onChanged={(data: Data) => {
        if (!canValidate && data.body.length || data.image?.file || data.mentionedUsers.length) {
          setCanValidate(true);
        }
        setBodyData(data);
        onChangeGetTags();
      }} data-sentry-element="ContentEditor" data-sentry-source-file="writePostEditorAndroid.tsx" />
      </PostContainer>
      <WritePostErrors dataTestId="write-post-body-error" errors={errors.body} data-sentry-element="WritePostErrors" data-sentry-source-file="writePostEditorAndroid.tsx" />
      {children}
      <SubmitButtonWrapper data-sentry-element="SubmitButtonWrapper" data-sentry-source-file="writePostEditorAndroid.tsx">
        <SubmitButton ariaLabel="Submit post" disabled={!!errors.body?.length && !errors.body[0]?.submitAllowed || !!errors.title?.length} role="submit" onClick={() => {
        setIsSubmit(true);
      }} data-sentry-element="SubmitButton" data-sentry-source-file="writePostEditorAndroid.tsx">
          {i18n.t('Post')}
        </SubmitButton>
      </SubmitButtonWrapper>
    </>;
}